import { auth0Config } from '../config/auth0-config';

interface SignupParams {
  email: string;
  password: string;
  name?: string;
}

interface LoginParams {
  email: string;
  password: string;
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
}

interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    username: string;
  };
}

interface SignupResponse {
  _id: string;
  email: string;
  email_verified: boolean;
}

interface Auth0Error {
  error?: string;
  error_description?: string;
  name?: string;
  code?: string;
  description?: string;
  statusCode?: number;
  message?: string;
}

/**
 * Service for making direct Auth0 Authentication API calls
 */
export class Auth0ApiService {
  private domain: string;
  private clientId: string;
  private audience: string;

  constructor() {
    this.domain = auth0Config.domain;
    this.clientId = auth0Config.clientId;
    this.audience = auth0Config.authorizationParams.audience || `https://${this.domain}/api/v2/`;
  }

  /**
   * Sign up a new user with email and password
   */
  async signup(params: SignupParams): Promise<SignupResponse> {
    const response = await fetch(`https://${this.domain}/dbconnections/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: this.clientId,
        email: params.email,
        password: params.password,
        connection: 'Username-Password-Authentication',
        name: params.name,
      }),
    });

    if (!response.ok) {
      const error: Auth0Error = await response.json();

      // Log full error for debugging
      console.error('Auth0 signup error:', error);

      // Check for specific error codes/descriptions
      const errorMessage = error.description || error.error_description || error.message || error.error || 'Signup failed';
      const errorCode = error.code || error.error;

      // Detect user already exists error (check message text explicitly)
      if (
        errorCode === 'user_exists' ||
        errorMessage.toLowerCase().includes('user already exists') ||
        errorMessage.toLowerCase().includes('already exists') ||
        errorMessage.toLowerCase().includes('user with that email already exists')
      ) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }

      // Detect password policy errors
      if (
        errorCode === 'invalid_password' ||
        (errorMessage.toLowerCase().includes('password') && errorMessage.toLowerCase().includes('policy')) ||
        errorMessage.toLowerCase().includes('password is too weak')
      ) {
        throw new Error('Password does not meet requirements. Please use a stronger password.');
      }

      // For generic invalid_signup, provide helpful context
      if (errorCode === 'invalid_signup') {
        throw new Error('Signup failed. This email may already be registered. Please try signing in, or use a different email.');
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Login with email and password using Resource Owner Password flow
   */
  async login(params: LoginParams): Promise<TokenResponse> {
    const response = await fetch(`https://${this.domain}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'password',
        username: params.email,
        password: params.password,
        client_id: this.clientId,
        audience: this.audience,
        scope: 'openid profile email',
      }),
    });

    if (!response.ok) {
      const error: Auth0Error = await response.json();
      throw new Error(error.error_description || error.error);
    }

    return response.json();
  }

  /**
   * Get user info from access token
   */
  async getUserInfo(accessToken: string): Promise<any> {
    const response = await fetch(`https://${this.domain}/userinfo`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get user info');
    }

    return response.json();
  }

  /**
   * Start passwordless authentication (for email code flow)
   */
  async startPasswordless(email: string): Promise<void> {
    const response = await fetch(`https://${this.domain}/passwordless/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: this.clientId,
        connection: 'email',
        email,
        send: 'code',
      }),
    });

    if (!response.ok) {
      const error: Auth0Error = await response.json();
      throw new Error(error.error_description || error.error);
    }
  }

  /**
   * Verify passwordless code
   */
  async verifyPasswordlessCode(email: string, code: string): Promise<TokenResponse> {
    const response = await fetch(`https://${this.domain}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'http://auth0.com/oauth/grant-type/passwordless/otp',
        client_id: this.clientId,
        username: email,
        otp: code,
        realm: 'email',
        audience: this.audience,
        scope: 'openid profile email',
      }),
    });

    if (!response.ok) {
      const error: Auth0Error = await response.json();
      throw new Error(error.error_description || error.error);
    }

    return response.json();
  }
}

export const auth0Api = new Auth0ApiService();

/**
 * Backend API Service for authentication and WebAuthn/Passkey operations
 */
class BackendApiService {
  private backendUrl: string | null = null;

  constructor() {
    // Backend URL will be loaded from runtime config on first use
  }

  private async getBackendUrl(): Promise<string> {
    if (this.backendUrl) {
      return this.backendUrl;
    }

    // Dynamically import to avoid circular dependencies
    const { getBackendUrl } = await import('../config/runtime-config');
    this.backendUrl = await getBackendUrl();
    return this.backendUrl;
  }

  /**
   * Sign up with email, username, and password
   */
  async signup(email: string, username: string, password: string): Promise<{ token: string; user: { id: string; email: string; username: string } }> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Signup failed');
    }

    const data = await response.json();
    if (data.status !== 'success') {
      throw new Error(data.message || 'Signup failed');
    }

    return { token: data.token, user: data.user };
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<{ token: string; user: { id: string; email: string; username: string } }> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    if (data.status !== 'success') {
      throw new Error(data.message || 'Login failed');
    }

    return { token: data.token, user: data.user };
  }

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<{ reset_url?: string }> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send reset email');
    }

    const data = await response.json();
    if (data.status !== 'success') {
      throw new Error(data.message || 'Failed to send reset email');
    }

    return data;
  }

  /**
   * Reset password with token
   */
  async resetPassword(email: string, token: string, newPassword: string): Promise<void> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, token, new_password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset password');
    }

    const data = await response.json();
    if (data.status !== 'success') {
      throw new Error(data.message || 'Failed to reset password');
    }
  }

  /**
   * Get passkey registration options from backend
   */
  async getPasskeyRegistrationOptions(email: string, username: string): Promise<{ status: string; options: any }> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/register/options`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, username }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get passkey registration options');
    }

    return response.json();
  }

  /**
   * Verify passkey registration credential
   */
  async verifyPasskeyRegistration(email: string, credential: any): Promise<AuthResponse> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/register/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, credential }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to verify passkey registration');
    }

    return response.json();
  }

  /**
   * Get passkey authentication options from backend
   */
  async getPasskeyAuthenticationOptions(email: string): Promise<{ status: string; options: any }> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/login/options`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get passkey authentication options');
    }

    return response.json();
  }

  /**
   * Verify passkey authentication credential
   */
  async verifyPasskeyAuthentication(email: string, credential: any): Promise<AuthResponse> {
    const backendUrl = await this.getBackendUrl();
    const response = await fetch(`${backendUrl}/login/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, credential }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to verify passkey authentication');
    }

    return response.json();
  }
}

export const backendApi = new BackendApiService();
